'use client'

import { useMemo, useState } from 'react'

import { shouldSubscribeToLiveMarketData } from '@/lib/venue-market-data'
import { useVenueHealthStreams } from '@/service/ws/use-venue-health-stream'
import { Exchange } from '@/types/generated/common/types'
import { VenueHealth } from '@/types/generated/public_api'

/**
 * Filters ticker/chart subscriptions using the venue's public-data uptime.
 * The health stream stays subscribed while market data is paused, allowing an
 * OPEN transition to restore the affected subscriptions automatically.
 */
export function useLiveVenuePayloads<TPayload extends { exchange: Exchange }>(
  payloads: readonly TPayload[],
  enabled = true
): TPayload[] {
  const exchanges = useMemo(
    () =>
      Array.from(
        new Set(
          payloads
            .map((payload) => payload.exchange)
            .filter(
              (exchange) =>
                exchange !== Exchange.EXCHANGE_UNSPECIFIED && exchange !== Exchange.UNRECOGNIZED
            )
        )
      ).sort((a, b) => a - b),
    [payloads]
  )
  const healthPayloads = useMemo(() => exchanges.map((exchange) => ({ exchange })), [exchanges])
  const activeHealthPayloads = enabled ? healthPayloads : []
  const [healthByExchange, setHealthByExchange] = useState<ReadonlyMap<Exchange, VenueHealth>>(
    new Map()
  )

  useVenueHealthStreams({
    connected: activeHealthPayloads.length > 0,
    payloads: activeHealthPayloads,
    onMessage: (health) => {
      setHealthByExchange((current) => {
        const previous = current.get(health.exchange)
        if (previous?.data === health.data) return current

        const next = new Map(current)
        next.set(health.exchange, health)
        return next
      })
    },
  })

  return useMemo(() => {
    if (!enabled) return []
    return payloads.filter((payload) =>
      shouldSubscribeToLiveMarketData(healthByExchange.get(payload.exchange))
    )
  }, [enabled, healthByExchange, payloads])
}
